import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import {
  WITHIN_REACH_TONE_MAP, ESCAPE_HATCH_POSITIVE_THRESHOLD,
} from "./constants";

// Global "Universities within reach" escape-hatch. Fires when fewer than
// 30% of the applicant's matches are Likely+Target — i.e. the student
// is broadly stretched, not just weak in one country. Handles both
// states: (a) unloaded CTA button + (b) loaded list + WUA+ signup.
//
// Parent owns the async `loadWithinReach` state so retry semantics
// stay simple.
export default function WithinReachPanel({
  summary,
  withinReach,
  withinReachLoading,
  onLoad,
  intendedSubject,
}) {
  const shouldRender = (() => {
    const total = summary.total_matches || 0;
    if (total === 0) return false;
    const positive = summary.countries.reduce(
      (n, c) => n + (c.likely || 0) + (c.target || 0), 0,
    );
    return (positive / total) < ESCAPE_HATCH_POSITIVE_THRESHOLD;
  })();
  if (!shouldRender) return null;

  return (
    <div
      className="mt-4 rounded-lg border border-[#4C1D95]/25 bg-[#4C1D95]/[0.04] p-4"
      data-testid="within-reach-panel"
    >
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div className="min-w-0 max-w-xl">
          <div className="label-uppercase !text-[10px] text-[#4C1D95]">
            Escape-hatch · closest to your profile
          </div>
          <div className="mt-1 text-sm text-stone-800 leading-snug">
            Most of your matches are a stretch. Here are the
            universities <em>closest</em> to your grade profile
            — the ones with the shortest gap, ranked using the
            same admission-chance model.
          </div>
        </div>
        {!withinReach && (
          <button
            type="button"
            onClick={onLoad}
            disabled={withinReachLoading}
            data-testid="within-reach-cta"
            className="text-[11px] font-underground-sc tracking-wider text-white bg-[#4C1D95] px-3 py-2 rounded-full hover:bg-[#3d1778] transition-colors disabled:opacity-50"
          >
            {withinReachLoading ? "Loading…" : "See closest matches →"}
          </button>
        )}
      </div>

      {withinReach && (
        <ul
          className="mt-4 divide-y divide-stone-100 border border-stone-200 rounded-md bg-white"
          data-testid="within-reach-list"
        >
          {withinReach.items.map((u) => {
            const pillCls = WITHIN_REACH_TONE_MAP[u.bucket] || "text-stone-700 bg-stone-50 border-stone-200";
            return (
              <li
                key={u.id}
                className="flex items-center justify-between gap-3 px-4 py-2.5"
                data-testid={`within-reach-row-${u.id}`}
              >
                <div className="min-w-0 flex-1">
                  <div className="text-sm text-stone-900 truncate">{u.name}</div>
                  <div className="mt-0.5 text-[10px] font-underground-sc tracking-wider text-stone-500">
                    {u.country}
                    {u.typical_grade ? ` · typical ${u.typical_grade}` : ""}
                    {u.min_grade ? ` · min ${u.min_grade}` : ""}
                  </div>
                </div>
                <span
                  className={`text-[10px] font-underground-sc tracking-wider px-2 py-1 rounded-full border shrink-0 ${pillCls}`}
                >
                  {u.label}
                </span>
              </li>
            );
          })}
          {withinReach.items.length === 0 && (
            <li className="px-4 py-4 text-[13px] text-stone-500">
              No universities in the catalogue currently match
              your profile more closely — try broadening your
              intended subject.
            </li>
          )}
        </ul>
      )}

      {/* WUA+ · Engaged tier CTA — the escape-hatch converts here
          rather than to email lead-capture. Only shown once the
          within-reach list is loaded so the target grade is known. */}
      {withinReach && withinReach.items?.length > 0 && (() => {
        const targetGrade = withinReach.items[0].typical_grade || "your target grade";
        const qs = new URLSearchParams({
          intent: "engaged",
          target_grade: targetGrade,
          source: "quick-check-escape-hatch",
        });
        if (intendedSubject) qs.set("intended_subject", intendedSubject);
        const href = `/wua-plus?${qs.toString()}`;
        return (
          <div
            className="mt-4 pt-4 border-t border-[#4C1D95]/15"
            data-testid="wua-plus-signup-cta"
          >
            <div className="flex items-center gap-2 mb-2">
              <span
                className="rounded-full bg-[#4C1D95] text-white text-[9px] font-underground-sc tracking-wider px-2 py-0.5"
                data-testid="wua-plus-badge"
              >
                WUA+
              </span>
              <span className="text-[10px] font-underground-sc tracking-wider text-stone-500">
                Engaged tier · 1:1 tutor bolt-on
              </span>
            </div>
            <div className="text-[12px] text-stone-800 leading-snug max-w-2xl">
              The universities above are within reach with the right
              grade lift. Join <span className="font-semibold">WhichUniversityAdvisor+</span> —
              the <span className="font-semibold">Engaged</span> tier
              adds proactive AI, a counsellor-led strategy debrief, and
              direct access to vetted tutors who can lift your predicted
              grades to <span className="font-semibold">{targetGrade}</span>.
            </div>
            <div className="mt-3 flex flex-wrap items-center gap-3">
              <Link
                to={href}
                data-testid="pearsana-lead-cta"
                className="inline-flex items-center gap-1.5 text-[12px] font-underground-sc tracking-wider text-white bg-[#4C1D95] px-4 py-2 rounded-full hover:bg-[#3d1778] transition-colors"
              >
                Join WUA+ · Engaged
                <ArrowRight className="h-3 w-3" />
              </Link>
              <Link
                to="/plus"
                className="text-[11px] text-stone-500 hover:underline"
                data-testid="wua-plus-learn-more"
              >
                Compare tiers →
              </Link>
              <span className="text-[10px] font-underground-sc tracking-wider text-stone-400">
                Annual subscription · bespoke quote · no card required
              </span>
            </div>
          </div>
        );
      })()}
    </div>
  );
}
